import type React from "react";
import { useLoaderData, type LoaderFunction } from "react-router-dom";
import { LinkifiedDescription } from "../components/LinkifiedDescriptions";
import { PerformanceCard } from "../components/PerformanceCard";
import { PlayerWidget } from "../components/PlayerWidget";
import { FallbackImage } from "../components/FallbackImage";
import {
  getArtistById,
  getDayById,
  getPerformancesByArtistId,
  getStageById,
  loadFestivalData,
} from "../services/dataService";
import type { Artist, Day, Performance, Stage } from "../types";
import { BasePage } from "./BasePage";

type ArtistPageData = Artist & {
  performances: Array<Performance & { day: Day | undefined; stage: Stage | undefined }>;
};

export const getEnrichedArtist = (id: string): ArtistPageData | undefined => {
  const artist = getArtistById(id);
  if (!artist) return undefined;

  const artistPerformances = getPerformancesByArtistId(id).map((performance) => ({
    ...performance,
    day: getDayById(performance.dayId),
    stage: getStageById(performance.stageId),
  }));

  return { ...artist, performances: artistPerformances };
};

export const loadArtistPageData: LoaderFunction<ArtistPageData> = async ({ params }) => {
  await loadFestivalData();
  const artistId = params.id;
  if (!artistId) {
    throw new Response("Artist ID is required", { status: 400 });
  }
  const artist = getEnrichedArtist(artistId);
  if (!artist) {
    throw new Response("Artist not found", { status: 404 });
  }
  return artist;
};

export const ArtistPage: React.FC = () => {
  const artist = useLoaderData() as ArtistPageData;

  return (
    <BasePage>
      <div className="space-y-6">
        {/* Artist Header Banner */}
        <div className="relative rounded-2xl overflow-hidden border border-white/5 bg-[#222222]/65">
          <div
            className="h-60 bg-black/40 flex items-end justify-start relative z-10 animate-bg-scroll"
            style={{
              backgroundImage: artist.image ? `url(${artist.image})` : "none",
              backgroundSize: "cover",
            }}
          >
            {/* If no image, show the fallback image leveraging the playerUrl */}
            {!artist.image && (
              <FallbackImage playerUrl={artist.playerUrl} variant="banner" alt={artist.name} />
            )}

            {/* Dark Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent z-0" />

            <div className="p-6 relative z-10 w-full flex items-center justify-between gap-4">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-main">
                  Előadó
                </span>
                <h2 className="text-3xl font-extrabold font-['Unbounded'] text-white uppercase tracking-tight drop-shadow-[0_0_10px_rgba(255,255,255,0.15)] mt-0.5">
                  {artist.name}
                </h2>
                {(artist.genre || artist.collective) && (
                  <p className="text-xs font-semibold text-accent mt-1.5 uppercase tracking-wider drop-shadow-md">
                    {artist.genre} {artist.genre && artist.collective && "•"} {artist.collective}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description / Bio */}
        {artist.description && (
          <div className="bg-[#222222]/65 border border-white/5 p-5 rounded-2xl backdrop-blur-md">
            <h3 className="font-['Unbounded'] text-sm font-bold text-accent mb-3.5 uppercase tracking-wider">
              <span className="ellipse-highlight-before-3 ellipse-highlight-after-1">
                Bemutatkozás
              </span>
            </h3>
            <div className="prose prose-invert max-w-none text-accent/90 text-sm leading-relaxed font-normal">
              <LinkifiedDescription description={artist.description} />
            </div>
          </div>
        )}

        {/* Player Embed */}
        {artist.playerUrl && (
          <div className="bg-[#222222]/65 border border-white/5 p-5 rounded-2xl backdrop-blur-md">
            <h3 className="font-['Unbounded'] text-sm font-bold text-main mb-3.5 uppercase tracking-wider">
              <span className="ellipse-highlight-before-2 ellipse-highlight-after-3">
                Zene / Mix
              </span>
            </h3>
            <PlayerWidget url={artist.playerUrl} />
          </div>
        )}

        <div className="mt-8">
          <h3 className="font-['Unbounded'] text-base font-bold text-main mb-4 uppercase tracking-wider pl-1">
            Események
          </h3>
          <div className="space-y-3">
            {artist.performances.map((performance) => (
              <PerformanceCard
                key={performance.id}
                performance={{
                  ...performance,
                  artist: {
                    id: artist.id,
                    name: artist.name,
                    qualifier: artist.qualifier,
                    collective: artist.collective,
                    description: artist.description,
                    genre: artist.genre,
                  },
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </BasePage>
  );
};

export default ArtistPage;
